import { createElement } from 'react';
import { Button } from '@reflex/ui/components/ui/button';
import { cn } from '@reflex/ui/lib/utils';
import { getApiUrl, saveReturnPath } from '@/lib/api';
import { getAuthProviderIcon } from '@/lib/plugin-registry';

function ProviderIcon({ name }: { name: string }) {
  const icon = getAuthProviderIcon(name);
  if (icon) return createElement(icon, { className: 'size-5' });
  return (
    <img
      src={`/plugin-icons/plugin-icon-auth-${name}.png`}
      alt=""
      className="size-5 object-contain"
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
}

interface SignInWithProviderButtonProps {
  /** Provider name as the API knows it (e.g. `github`, `google`, `linear`, `slack`). */
  name: string;
  /** Display label as advertised by `/auth/me` (`GitHub`, `Google`, …). */
  label: string;
  /** Visual emphasis. The first provider on a page is typically `primary`. */
  variant?: 'primary' | 'outline';
  /** Match the LoginPage button density (`lg`) by default — matches the rest of the auth chrome. */
  size?: 'default' | 'sm' | 'lg';
  /**
   * Whether to remember the current path so the auth callback can return the
   * user here (login flow). Defaults to true so callers don't have to opt in;
   * pass `false` for setup-flow surfaces that always redirect to `/setup`.
   */
  rememberReturnPath?: boolean;
  className?: string;
}

/**
 * "Continue with X" button — uses the provider's plugin-registered icon
 * (or the bundled `/plugin-icons/plugin-icon-auth-{name}.png` fallback)
 * and a standardized label, then links out to the provider's OAuth
 * start route. Every OAuth flow is create-or-sign-in, so one button
 * serves returning users, first-time visitors, and invitees alike.
 */
export function SignInWithProviderButton({
  name,
  label,
  variant = 'outline',
  size = 'lg',
  rememberReturnPath = true,
  className,
}: SignInWithProviderButtonProps) {
  const href = getApiUrl(`/auth/${name}`);
  return (
    <Button
      asChild
      variant={variant === 'primary' ? 'default' : 'outline'}
      size={size}
      className={cn('w-full justify-center', className)}
      data-testid={`sign-in-with-${name}`}
    >
      <a href={href} onClick={rememberReturnPath ? saveReturnPath : undefined}>
        <ProviderIcon name={name} />
        Continue with {label}
      </a>
    </Button>
  );
}
